// Helper to convert between raw string with tokens like {first_name}
// and TipTap JSON doc using Mention nodes with ids like 'first-name'.

function tokenToMentionId(token) {
  // token comes without braces, e.g., 'first_name'
  return token.replaceAll('_', '-');
}

function mentionIdToToken(id) {
  // id like 'first-name' -> 'first_name'
  return id.replaceAll('-', '_');
}

export function toTipTapDoc(raw) {
  if (!raw || typeof raw !== 'string') {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    };
  }

  // Split by newlines to create separate paragraphs for each line
  // This ensures both Enter (paragraph breaks) and Shift+Enter (hardBreaks)
  // are treated consistently as line breaks
  const lines = raw.split('\n');
  const paragraphs = [];

  lines.forEach((line) => {
    const paraContent = [];

    // Parse tokens like {first_name} within each line
    const regex = /\{([a-zA-Z0-9_]+)\}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(line)) !== null) {
      const before = line.slice(lastIndex, match.index);
      if (before) {
        paraContent.push({ type: 'text', text: before });
      }
      const token = match[1];
      paraContent.push({
        type: 'mention',
        attrs: { id: tokenToMentionId(token) },
      });
      lastIndex = regex.lastIndex;
    }

    const after = line.slice(lastIndex);
    if (after) {
      paraContent.push({ type: 'text', text: after });
    }

    // Create a paragraph for each line (even empty lines)
    // If the line is empty, create an empty paragraph
    paragraphs.push({
      type: 'paragraph',
      content: paraContent.length > 0 ? paraContent : undefined,
    });
  });

  return {
    type: 'doc',
    content: paragraphs,
  };
}

export function fromTipTapDoc(doc) {
  if (!doc || doc.type !== 'doc') return '';

  const paragraphTexts = [];

  const emitFromNode = (node) => {
    if (!node) return '';
    if (node.type === 'text' && typeof node.text === 'string') {
      return node.text;
    }
    if (node.type === 'hardBreak') {
      return '\n';
    }
    if (node.type === 'mention') {
      const id = node.attrs?.id || '';
      const token = mentionIdToToken(id);
      return `{${token}}`;
    }
    if (Array.isArray(node.content)) {
      return node.content.map(emitFromNode).join('');
    }
    return '';
  };

  // Process each paragraph separately and join with newlines
  // This handles both paragraph breaks (Enter) and hardBreaks (Shift+Enter) consistently
  (doc.content || []).forEach((node) => {
    if (node.type === 'paragraph') {
      const paragraphText = emitFromNode(node);
      paragraphTexts.push(paragraphText);
    } else {
      // Handle other node types at document level if needed
      const nodeText = emitFromNode(node);
      if (nodeText) {
        paragraphTexts.push(nodeText);
      }
    }
  });

  return paragraphTexts.join('\n');
}

// Helper function to convert {first_name} to @first-name for display purposes
export function convertToDisplayFormat(text) {
  if (!text || typeof text !== 'string') return text;

  // Only replace {first_name} specifically with @first-name
  return text.replace(/\{first_name\}/g, '@first-name');
}
